/**
 * GRN Enterprise Filters Component
 * Phase 3: Advanced filtering for GRN Management
 * 
 * Features:
 * - Date range picker
 * - Supplier dropdown with search
 * - Status multi-select
 * - Aging filter (visual buckets)
 * - Exception toggle
 * - Quick presets
 * - Active filter badges with clear
 * 
 * @version 1.0.0
 * @date January 2026
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Search,
  Filter,
  X,
  Calendar,
  Building2,
  Clock,
  AlertTriangle,
  ChevronDown,
  RotateCcw,
  Zap,
  FileText,
  Check
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../../components/ui/command';
import { Calendar as CalendarComponent } from '../../../components/ui/calendar';
import { Switch } from '../../../components/ui/switch';
import { Separator } from '../../../components/ui/separator';
import { cn } from '../../../lib/utils';
import { format } from 'date-fns';

// Status options with colors
const STATUS_OPTIONS = [
  { value: 'DRAFT', label: 'Draft', color: 'bg-gray-100 text-gray-700 border-gray-300' },
  { value: 'RECEIVED', label: 'Received', color: 'bg-blue-100 text-blue-700 border-blue-300' },
  { value: 'INSPECTING', label: 'Inspecting', color: 'bg-amber-100 text-amber-700 border-amber-300' },
  { value: 'COMPLETED', label: 'Completed', color: 'bg-green-100 text-green-700 border-green-300' },
];

// Aging bucket options
const AGING_OPTIONS = [
  { value: 'all', label: 'All Ages', icon: Clock, color: 'text-gray-500' },
  { value: 'lt24h', label: '< 24 hours', icon: Zap, color: 'text-green-600' },
  { value: '24to48h', label: '24-48 hours', icon: Clock, color: 'text-amber-600' },
  { value: '48hto7d', label: '48h - 7 days', icon: Clock, color: 'text-orange-600' },
  { value: 'gt7d', label: '> 7 days', icon: AlertTriangle, color: 'text-red-600' },
  { value: 'overdue', label: 'Overdue Only', icon: AlertTriangle, color: 'text-red-700' },
];

// Quick filter presets
const QUICK_PRESETS = [
  { id: 'pending', label: 'Pending Action', filters: { status: 'DRAFT' } },
  { id: 'inspecting', label: 'In Inspection', filters: { status: 'INSPECTING' } },
  { id: 'overdue', label: 'Overdue', filters: { aging: 'overdue' } },
  { id: 'today', label: 'Today', filters: { datePreset: 'today' } },
  { id: 'thisWeek', label: 'This Week', filters: { datePreset: 'week' } },
];

/**
 * GRNFilters Component
 * 
 * @param {Object} props
 * @param {Object} props.filters - Current filter values
 * @param {Function} props.onFiltersChange - Callback when filters change
 * @param {Array} props.suppliers - List of suppliers for dropdown
 * @param {boolean} props.loading - Loading state
 * @param {Object} props.filterSummary - Summary stats from backend
 */
const GRNFilters = ({ 
  filters = {}, 
  onFiltersChange, 
  suppliers = [],
  loading = false,
  filterSummary = {}
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [supplierOpen, setSupplierOpen] = useState(false);
  const [fromDateOpen, setFromDateOpen] = useState(false);
  const [toDateOpen, setToDateOpen] = useState(false);
  
  // Use controlled input derived from filters - avoid internal state sync issues
  const searchInputValue = useMemo(() => filters.grn_search || '', [filters.grn_search]);
  const [localSearch, setLocalSearch] = useState(searchInputValue);
  const debounceTimerRef = React.useRef(null);

  // Debounce search input with proper cleanup
  const handleSearchChange = (value) => {
    setLocalSearch(value);
    
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    debounceTimerRef.current = setTimeout(() => {
      onFiltersChange({ ...filters, grn_search: value || undefined });
    }, 400);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Sync local search with filter changes from parent (e.g., clear all)
  useEffect(() => {
    if (localSearch !== searchInputValue) {
      setLocalSearch(searchInputValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInputValue]);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.status) count++;
    if (filters.supplier_name) count++;
    if (filters.po_number) count++;
    if (filters.grn_search) count++;
    if (filters.from_date || filters.to_date) count++;
    if (filters.aging) count++;
    if (filters.has_exceptions) count++;
    return count;
  }, [filters]);

  // Handle filter change
  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters };
    if (value === '' || value === null || value === undefined) {
      delete newFilters[key];
    } else {
      newFilters[key] = value;
    }
    onFiltersChange(newFilters);
  };

  // Handle date preset
  const handleDatePreset = (preset) => {
    const now = new Date();
    let from_date, to_date;
    
    switch (preset) {
      case 'today':
        from_date = to_date = format(now, 'yyyy-MM-dd');
        break;
      case 'week':
        const weekAgo = new Date(now);
        weekAgo.setDate(weekAgo.getDate() - 7);
        from_date = format(weekAgo, 'yyyy-MM-dd');
        to_date = format(now, 'yyyy-MM-dd');
        break;
      case 'month':
        const monthAgo = new Date(now);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        from_date = format(monthAgo, 'yyyy-MM-dd');
        to_date = format(now, 'yyyy-MM-dd');
        break;
      default:
        return;
    }
    
    onFiltersChange({ ...filters, from_date, to_date });
  };

  // Apply quick preset
  const applyPreset = (preset) => {
    const newFilters = {};
    
    if (preset.filters.datePreset) {
      handleDatePreset(preset.filters.datePreset);
      return;
    }
    
    Object.entries(preset.filters).forEach(([key, value]) => {
      if (value) newFilters[key] = value;
    });
    
    onFiltersChange(newFilters);
  };

  // Clear all filters
  const clearAllFilters = () => {
    setLocalSearch('');
    onFiltersChange({});
  };

  // Clear single filter
  const clearFilter = (key) => {
    const newFilters = { ...filters };
    delete newFilters[key];
    if (key === 'from_date') delete newFilters.to_date;
    if (key === 'to_date') delete newFilters.from_date;
    onFiltersChange(newFilters);
  };

  return (
    <div className="space-y-3" data-testid="grn-filters">
      {/* Main Filter Bar */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Search Input */}
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search GRN number..."
            value={localSearch}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-10 h-9"
            data-testid="grn-search-input"
          />
          {localSearch && (
            <button
              onClick={() => handleSearchChange('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Status Filter */}
        <Select
          value={filters.status || 'all'}
          onValueChange={(value) => handleFilterChange('status', value === 'all' ? '' : value)}
        >
          <SelectTrigger className="w-[140px] h-9" data-testid="grn-status-filter">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {STATUS_OPTIONS.map((status) => (
              <SelectItem key={status.value} value={status.value}>
                <div className="flex items-center gap-2">
                  <span className={cn("w-2 h-2 rounded-full", status.color.split(' ')[0])} />
                  {status.label}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Supplier Combobox */}
        <Popover open={supplierOpen} onOpenChange={setSupplierOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={supplierOpen}
              className="w-[180px] h-9 justify-between font-normal"
              data-testid="grn-supplier-filter"
            >
              <Building2 className="w-4 h-4 mr-2 text-gray-400 flex-shrink-0" />
              <span className="truncate">
                {filters.supplier_name || 'All Suppliers'}
              </span>
              <ChevronDown className="ml-auto h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[280px] p-0" align="start">
            <Command>
              <CommandInput placeholder="Search supplier..." />
              <CommandList>
                <CommandEmpty>No supplier found.</CommandEmpty>
                <CommandGroup>
                  <CommandItem
                    value=""
                    onSelect={() => {
                      handleFilterChange('supplier_name', '');
                      setSupplierOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        !filters.supplier_name ? "opacity-100" : "opacity-0"
                      )}
                    />
                    All Suppliers
                  </CommandItem>
                  {suppliers.map((supplier) => (
                    <CommandItem
                      key={supplier.name}
                      value={supplier.name}
                      onSelect={(value) => {
                        handleFilterChange('supplier_name', value);
                        setSupplierOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          filters.supplier_name === supplier.name ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <div className="flex-1 truncate">
                        <span>{supplier.name}</span>
                        <span className="text-xs text-gray-400 ml-2">({supplier.grn_count})</span>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {/* Aging Filter */}
        <Select
          value={filters.aging || 'all'}
          onValueChange={(value) => handleFilterChange('aging', value === 'all' ? '' : value)}
        >
          <SelectTrigger className="w-[150px] h-9" data-testid="grn-aging-filter">
            <SelectValue placeholder="All Ages" />
          </SelectTrigger>
          <SelectContent>
            {AGING_OPTIONS.map((option) => {
              const Icon = option.icon;
              return (
                <SelectItem key={option.value} value={option.value}>
                  <div className="flex items-center gap-2">
                    <Icon className={cn("w-4 h-4", option.color)} />
                    {option.label}
                  </div>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>

        {/* Expand/Collapse More Filters */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsExpanded(!isExpanded)}
          className={cn(
            "h-9",
            isExpanded && "bg-blue-50 border-blue-200"
          )}
          data-testid="grn-more-filters-btn"
        >
          <Filter className="w-4 h-4 mr-2" />
          More
          {activeFilterCount > 0 && (
            <Badge className="ml-2 h-5 w-5 p-0 flex items-center justify-center bg-blue-600 text-white">
              {activeFilterCount}
            </Badge>
          )}
        </Button>

        {/* Clear All */}
        {activeFilterCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearAllFilters}
            className="h-9 text-gray-500 hover:text-gray-700"
            data-testid="grn-clear-filters-btn"
          >
            <RotateCcw className="w-4 h-4 mr-1" />
            Clear
          </Button>
        )}
      </div>

      {/* Expanded Filters Section */}
      {isExpanded && (
        <div className="p-4 bg-gray-50 rounded-lg border space-y-4 animate-in slide-in-from-top-2 duration-200">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Date Range */}
            <div className="space-y-2">
              <Label className="text-xs font-medium text-gray-600">Date Range</Label>
              <div className="flex gap-2">
                {/* From Date */}
                <Popover open={fromDateOpen} onOpenChange={setFromDateOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "flex-1 h-9 justify-start text-left font-normal",
                        !filters.from_date && "text-muted-foreground"
                      )}
                    >
                      <Calendar className="mr-2 h-4 w-4" />
                      {filters.from_date ? format(new Date(filters.from_date), 'MMM d') : 'From'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={filters.from_date ? new Date(filters.from_date) : undefined}
                      onSelect={(date) => {
                        handleFilterChange('from_date', date ? format(date, 'yyyy-MM-dd') : '');
                        setFromDateOpen(false);
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>

                {/* To Date */}
                <Popover open={toDateOpen} onOpenChange={setToDateOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "flex-1 h-9 justify-start text-left font-normal",
                        !filters.to_date && "text-muted-foreground"
                      )}
                    >
                      <Calendar className="mr-2 h-4 w-4" />
                      {filters.to_date ? format(new Date(filters.to_date), 'MMM d') : 'To'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={filters.to_date ? new Date(filters.to_date) : undefined}
                      onSelect={(date) => {
                        handleFilterChange('to_date', date ? format(date, 'yyyy-MM-dd') : '');
                        setToDateOpen(false);
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              {/* Quick date presets */}
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 text-xs px-2"
                  onClick={() => handleDatePreset('today')}
                >
                  Today
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 text-xs px-2"
                  onClick={() => handleDatePreset('week')}
                >
                  Week
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 text-xs px-2"
                  onClick={() => handleDatePreset('month')}
                >
                  Month
                </Button>
              </div>
            </div>

            {/* PO Number Search */}
            <div className="space-y-2">
              <Label className="text-xs font-medium text-gray-600">PO Number</Label>
              <div className="relative">
                <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search PO..."
                  value={filters.po_number || ''}
                  onChange={(e) => handleFilterChange('po_number', e.target.value)}
                  className="pl-10 h-9"
                  data-testid="grn-po-filter"
                />
              </div>
            </div>

            {/* Exception Toggle */}
            <div className="space-y-2">
              <Label className="text-xs font-medium text-gray-600">Exceptions</Label>
              <div className="flex items-center gap-3 h-9 px-3 bg-white rounded-md border">
                <Switch
                  id="has-exceptions"
                  checked={filters.has_exceptions || false}
                  onCheckedChange={(checked) => handleFilterChange('has_exceptions', checked || undefined)}
                  data-testid="grn-exceptions-toggle"
                />
                <Label htmlFor="has-exceptions" className="text-sm cursor-pointer">
                  Show only with exceptions
                </Label>
              </div>
            </div>

            {/* Quick Presets */}
            <div className="space-y-2">
              <Label className="text-xs font-medium text-gray-600">Quick Presets</Label>
              <div className="flex flex-wrap gap-1">
                {QUICK_PRESETS.map((preset) => (
                  <Button
                    key={preset.id}
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => applyPreset(preset)}
                    data-testid={`grn-preset-${preset.id}`}
                  >
                    {preset.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Active Filter Badges */}
      {activeFilterCount > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-gray-500">Active:</span>
          
          {filters.status && (
            <Badge variant="secondary" className="h-6 gap-1 pr-1">
              Status: {filters.status}
              <button
                onClick={() => clearFilter('status')}
                className="ml-1 hover:bg-gray-300 rounded-full p-0.5"
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          )}
          
          {filters.supplier_name && (
            <Badge variant="secondary" className="h-6 gap-1 pr-1 max-w-[200px]">
              <span className="truncate">Supplier: {filters.supplier_name}</span>
              <button
                onClick={() => clearFilter('supplier_name')}
                className="ml-1 hover:bg-gray-300 rounded-full p-0.5 flex-shrink-0"
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          )}
          
          {filters.grn_search && (
            <Badge variant="secondary" className="h-6 gap-1 pr-1">
              Search: &quot;{filters.grn_search}&quot;
              <button
                onClick={() => {
                  setLocalSearch('');
                  clearFilter('grn_search');
                }}
                className="ml-1 hover:bg-gray-300 rounded-full p-0.5"
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          )}
          
          {filters.po_number && (
            <Badge variant="secondary" className="h-6 gap-1 pr-1">
              PO: {filters.po_number}
              <button
                onClick={() => clearFilter('po_number')}
                className="ml-1 hover:bg-gray-300 rounded-full p-0.5"
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          )}
          
          {(filters.from_date || filters.to_date) && (
            <Badge variant="secondary" className="h-6 gap-1 pr-1">
              Date: {filters.from_date || '...'} to {filters.to_date || '...'}
              <button
                onClick={() => clearFilter('from_date')}
                className="ml-1 hover:bg-gray-300 rounded-full p-0.5"
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          )}
          
          {filters.aging && (
            <Badge variant="secondary" className="h-6 gap-1 pr-1">
              Age: {AGING_OPTIONS.find(a => a.value === filters.aging)?.label || filters.aging}
              <button
                onClick={() => clearFilter('aging')}
                className="ml-1 hover:bg-gray-300 rounded-full p-0.5"
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          )}
          
          {filters.has_exceptions && (
            <Badge variant="secondary" className="h-6 gap-1 pr-1 bg-amber-100 text-amber-800">
              Has Exceptions
              <button
                onClick={() => clearFilter('has_exceptions')}
                className="ml-1 hover:bg-amber-200 rounded-full p-0.5"
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          )}
          
          {/* Filter Summary from Backend */}
          {filterSummary?.total_in_filter > 0 && (
            <span className="text-xs text-gray-500 ml-2">
              ({filterSummary.total_in_filter} results
              {filterSummary.overdue_count > 0 && (
                <span className="text-red-600 ml-1">• {filterSummary.overdue_count} overdue</span>
              )})
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default GRNFilters;
